import React from 'react';
import PropTypes from 'prop-types';

import { useSingleSiteSetting } from 'common/react/hooks/use-single-site-setting';
import { formatMatchDayLabel } from 'common/react/utils/status-badge-utils';
import {
    StatusBadge,
    SCORE_UPDATE_SHAPE,
    PERIOD_EVENT_SHAPE
} from './StatusBadge';
import { TeamRow } from './TeamRow';

const { MATCH_STATUS } = PULSE.app.common.CONSTANTS;

/**
 * Mobile (160px stacked tile) match card layout. Props passed straight through from MatchCard.jsx.
 *
 * @param {object} props - Props.
 * @param {object} props.match - The normalised match object from useMatchCardData.
 * @param {string} props.genericStatus - One of PULSE.app.common.CONSTANTS.MATCH_STATUS.
 * @param {boolean} [props.isPlaceholder] - True when either team is a placeholder.
 * @param {string} props.matchId - The match's providerId.
 * @param {object} [props.scoreUpdate] - { show, team, type } from useScoreUpdate.
 * @param {object} [props.periodEvent] - { show, period, phase } from usePeriodEvent.
 * @param {string} [props.fallbackIcon] - Fallback crest modifier for placeholder teams.
 * @returns {React.ReactElement} The Mobile layout.
 */
export const MatchCardMobile = ({
    match,
    genericStatus,
    isPlaceholder,
    matchId,
    scoreUpdate,
    periodEvent,
    fallbackIcon
}) => {
    const selectedRegion = useSingleSiteSetting('region');
    const showDate = genericStatus !== MATCH_STATUS.LIVE;

    return (
        <>
            <div className="flex w-full min-h-l items-center justify-between px-xs">
                <StatusBadge
                    match={match}
                    genericStatus={genericStatus}
                    isPlaceholder={isPlaceholder}
                    matchId={matchId}
                    scoreUpdate={scoreUpdate}
                    periodEvent={periodEvent}
                    compact
                />
                {showDate && (
                    <span className="label-m-semi-bold text-on-surface-variant">
                        {formatMatchDayLabel(
                            match.utcStartTime,
                            selectedRegion?.timezone
                        )}
                    </span>
                )}
            </div>
            <TeamRow
                teamPid={match.homeTeamPid}
                teamAbbr={match.homeTeamAbbr}
                score={match.homeScore}
                fallbackIcon={fallbackIcon}
            />
            <TeamRow
                teamPid={match.awayTeamPid}
                teamAbbr={match.awayTeamAbbr}
                score={match.awayScore}
                fallbackIcon={fallbackIcon}
            />
        </>
    );
};

MatchCardMobile.propTypes = {
    match: PropTypes.object.isRequired,
    genericStatus: PropTypes.string.isRequired,
    isPlaceholder: PropTypes.bool,
    matchId: PropTypes.string.isRequired,
    scoreUpdate: SCORE_UPDATE_SHAPE,
    periodEvent: PERIOD_EVENT_SHAPE,
    fallbackIcon: PropTypes.string
};
